import { TopNav } from "@dashboard/components/AppLayout";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { getApiUrl, getAppPlatformUrl } from "@dashboard/config";
import { postConfigurationUpdate } from "@dashboard/envSettings/services/configurationApi";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { Box, Spinner, Switch, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

export const WillCallShipping: React.FC = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const updateConfiguration = async (activeStatus: boolean) => {
    setLoading(true);

    try {
      const tenant = getApiUrl();

      const response = await postConfigurationUpdate({
        tenant: tenant,
        app_name: "will-call",
        configurations: {},
        is_active: activeStatus,
      });

      if (!response.ok) {
        throw new Error(`Failed to update configuration: ${response.statusText}`);
      }

      setIsActive(activeStatus);
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges),
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update configuration";

      notify({
        status: "error",
        text: errorMessage,
      });
      setIsActive(!activeStatus);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChange = (value: string) => {
    const newActiveStatus = value === "active";

    updateConfiguration(newActiveStatus);
  };

  // Load existing configuration from API
  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        const tenant = getApiUrl();

        if (!tenant) {
          setIsActive(false);
          setInitialLoading(false);

          return;
        }

        const response = await fetch(
          `${getAppPlatformUrl("app-configurations")}/app/get-configuration?tenant=${encodeURIComponent(tenant)}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          const result = await response.json();

          // Look for will-call app configuration
          const willCallApp = result.find((app: any) => app.app_name === "will-call");

          if (willCallApp) {
            setIsActive(willCallApp.is_active !== false);
          } else {
            // No will-call configuration found, default to inactive
            setIsActive(false);
          }
        } else {
          // API call failed, default to inactive
          setIsActive(false);
        }
      } catch (error) {
        // Error loading configuration, default to inactive
        setIsActive(false);
      } finally {
        setInitialLoading(false);
      }
    };

    loadConfiguration();
  }, []);

  if (initialLoading) {
    return (
      <ListPageLayout>
        <TopNav withoutBorder title="Will Call Shipping" />
        <DashboardCard paddingX={6}>
          <Box paddingY={6}>
            <Box display="flex" flexDirection="column" gap={4}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Text size={5} fontWeight="bold" marginBottom={2}>
                  Will Call Shipping Configuration
                </Text>
                <Text size={3} color="default2">
                  Enable or disable the Will Call shipping option for your store.
                </Text>
              </Box>
              <Box
                padding={4}
                borderWidth={1}
                borderColor="default1"
                borderStyle="solid"
                borderRadius={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={3}
                __minHeight="80px"
              >
                <Spinner />
                <Text size={3} color="default2">
                  Loading configuration...
                </Text>
              </Box>
            </Box>
          </Box>
        </DashboardCard>
      </ListPageLayout>
    );
  }

  return (
    <ListPageLayout>
      <TopNav withoutBorder title="Will Call Shipping" />
      <DashboardCard paddingX={6}>
        <Box paddingY={6}>
          <Box display="flex" flexDirection="column" gap={4}>
            <Box display="flex" flexDirection="column">
              <Text size={5} fontWeight="bold" marginBottom={2}>
                Will Call Shipping Configuration
              </Text>
              <Text size={3} color="default2">
                Enable or disable the Will Call shipping option for your store.
              </Text>
            </Box>

            <Box
              padding={4}
              borderWidth={1}
              borderColor="default1"
              borderStyle="solid"
              borderRadius={4}
            >
              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" alignItems="center" gap={3}>
                  <Text size={4} fontWeight="medium">
                    Enable Will Call Shipping
                  </Text>
                  {loading ? (
                    <Box display="flex" alignItems="center" gap={2}>
                      <Spinner />
                      <Text size={3} color="default2">
                        Updating...
                      </Text>
                    </Box>
                  ) : (
                    <Switch
                      value={isActive ? "active" : "inactive"}
                      onValueChange={handleToggleChange}
                      disabled={loading}
                      __height="32px"
                      __width="fit-content"
                      padding={0.5}
                    >
                      <Switch.Item
                        id="inactive"
                        value="inactive"
                        name="status"
                        disabled={loading}
                        marginRight={0.5}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          paddingX={2}
                          height="100%"
                        >
                          <Text size={2} fontWeight="medium">
                            Off
                          </Text>
                        </Box>
                      </Switch.Item>
                      <Switch.Item
                        id="active"
                        value="active"
                        name="status"
                        disabled={loading}
                        marginLeft={0.5}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          paddingX={2}
                          height="100%"
                        >
                          <Text size={2} fontWeight="medium">
                            On
                          </Text>
                        </Box>
                      </Switch.Item>
                    </Switch>
                  )}
                </Box>
                <Text size={3} color="default2">
                  Allow customers to pick up orders at your location
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>
      </DashboardCard>
    </ListPageLayout>
  );
};
